import React, { useState, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import PrivateRoute from './components/PrivateRoute';
import { NotificationsProvider } from './contexts/NotificationsContext';
import Header from './components/Header';
import Footer from './components/Footer';
import Onboarding, { hasSeenOnboarding } from './components/Onboarding';
import './App.css';

const HomePage = lazy(() => import('./components/HomePage'));
const Movies = lazy(() => import('./components/Movies'));
const Categories = lazy(() => import('./components/Categories'));
const CategoryPage = lazy(() => import('./components/CategoryPage'));
const MyList = lazy(() => import('./components/MyList'));
const Login = lazy(() => import('./components/Login'));
const Signup = lazy(() => import('./components/Signup'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const MovieDetail = lazy(() => import('./components/MovieDetail'));
const Watch = lazy(() => import('./components/Watch'));
const SearchResults = lazy(() => import('./components/SearchResults'));
const Profile = lazy(() => import('./components/Profile'));
const History = lazy(() => import('./components/History'));
const Originals = lazy(() => import('./components/Originals'));
const About = lazy(() => import('./components/About'));
const Help = lazy(() => import('./components/Help'));
const FAQ = lazy(() => import('./components/FAQ'));
const Contact = lazy(() => import('./components/Contact'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const Terms = lazy(() => import('./components/Terms'));

const NotFound = () => (
  <div className="min-h-screen bg-black flex items-center justify-center px-4">
    <div className="text-center">
      <h1 className="text-6xl font-bold text-oplixi-red mb-4">404</h1>
      <p className="text-white text-xl mb-2">Page Not Found</p>
      <p className="text-gray-400 mb-8">The page you're looking for doesn't exist.</p>
      <a href="/" className="bg-oplixi-red hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium no-underline transition-colors">
        Go Home
      </a>
    </div>
  </div>
);

const PageLoader = () => (
  <div className="min-h-screen bg-black flex items-center justify-center">
    <div className="flex flex-col items-center gap-4">
      <div className="text-oplixi-red font-bold text-2xl tracking-tight">OPLIXI</div>
      <div className="w-24 h-1 rounded-full overflow-hidden bg-white/10">
        <div className="h-full w-1/2 bg-oplixi-red rounded-full animate-[shimmer_1s_ease-in-out_infinite_alternate]"
          style={{ animation: 'pulse 1s ease-in-out infinite' }} />
      </div>
    </div>
  </div>
);

function App() {
  const [showOnboarding, setShowOnboarding] = useState(() => !hasSeenOnboarding());

  if (showOnboarding) {
    return <Onboarding onComplete={() => setShowOnboarding(false)} />;
  }

  return (
    <ErrorBoundary>
      <Router>
        <NotificationsProvider>
        <div className="app">
          <Header />
          <main className="main-content">
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/movies" element={<Movies />} />
                <Route path="/originals" element={<Originals />} />
                <Route path="/categories" element={<Categories />} />
                <Route path="/category" element={<CategoryPage />} />
                <Route path="/search" element={<SearchResults />} />
                <Route path="/mylist" element={<MyList />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/movie/:movieId" element={<MovieDetail />} />
                <Route path="/watch/:movieId" element={<Watch />} />
                <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
                <Route path="/history" element={<History />} />
                <Route path="/about" element={<About />} />
                <Route path="/help" element={<Help />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </main>
          <Footer />
        </div>
        </NotificationsProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
